import { BRIDGE_NAME, PLATFORM, WEBVIEW_APP } from './appBridge.constants'

export type TPlatform = (typeof PLATFORM)[keyof typeof PLATFORM]

export type TWebviewApp = (typeof WEBVIEW_APP)[keyof typeof WEBVIEW_APP]

interface IHandleBridgeErrorParams {
  message: string
  payload: unknown
  onError?: () => void
}

interface ICallAndroidBridgeParams {
  bridgeName: string
  /** 메서드명 (Mycle용 - 직접 호출 방식) */
  method?: string
  payload?: string
}

/**
 * 플랫폼 감지
 */
export const getPlatform = (): TPlatform | null => {
  if (typeof window === 'undefined') return null
  if (window.webkit) return PLATFORM.IOS
  if (window.MycleBridge || window.AppNavigator) return PLATFORM.ANDROID
  return null
}

/**
 * 브릿지 사용 가능 여부 확인
 */
export const isBridgeAvailable = (bridgeName: string): boolean => {
  if (typeof window === 'undefined') return false

  // iOS
  if (window.webkit?.messageHandlers?.[bridgeName]) return true

  // Android
  if (window[bridgeName as keyof Window]) return true

  return false
}

/**
 * 웹뷰를 띄운 네이티브 앱 판별 — 일반 브라우저면 null.
 * 브릿지 주입 여부로 판단하므로 클라이언트에서만 호출해야 한다.
 */
export const getWebviewApp = (): TWebviewApp | null => {
  if (isBridgeAvailable(BRIDGE_NAME.MYCLE)) return WEBVIEW_APP.MYCLE
  if (isBridgeAvailable(BRIDGE_NAME.PARTNERS)) return WEBVIEW_APP.PARTNERS
  return null
}

/**
 * 앱 웹뷰(마이클/파트너스) 실행 여부 — 서버에서는 false.
 * Android는 Activity마다 브릿지 주입이 조건부라
 * (예: 팝업·타이어예약·지도예약 웹뷰는 MycleBridge 미주입) 브릿지만으로는 놓친다.
 * 마이클 앱은 Activity와 무관하게 UA에 `Macarong/{버전}` 토큰을 붙이므로 이를 폴백으로 쓴다.
 */
export const isAppWebView = (): boolean => {
  if (typeof window === 'undefined') return false
  if (getPlatform() !== null || getWebviewApp() !== null) return true
  return /Macarong\//.test(navigator.userAgent)
}

/**
 * 에러 로깅 및 콜백 실행
 */
export const handleBridgeError = ({
  message: _message,
  payload: _payload,
  onError,
}: IHandleBridgeErrorParams): void => {
  onError?.()
}

interface ICallIOSBridgeParams {
  bridgeName: string
  payload: string
}

/**
 * iOS 브릿지 호출 (모든 브릿지 공통 - postMessage 방식)
 *
 * @example
 * // Partners
 * callIOSBridge({ bridgeName: 'AppNavigator', payload })
 * // → window.webkit.messageHandlers.AppNavigator.postMessage(payload)
 *
 * // Mycle
 * callIOSBridge({ bridgeName: 'MycleBridge', payload })
 * // → window.webkit.messageHandlers.MycleBridge.postMessage(payload)
 */
export const callIOSBridge = ({
  bridgeName,
  payload,
}: ICallIOSBridgeParams): void => {
  window.webkit.messageHandlers[bridgeName].postMessage(payload)
}

/**
 * Android 브릿지 호출
 *
 * @param bridgeName - 브릿지 이름 (MycleBridge | AppNavigator)
 * @param method - 메서드명 (Mycle 전용, 있으면 직접 호출 방식)
 * @param payload - JSON.stringify된 body
 *
 * @example
 * // Partners: postMessage 방식
 * callAndroidBridge({ bridgeName: 'AppNavigator', payload })
 * // → window.AppNavigator.postMessage(payload)
 *
 * // Mycle: 메서드 직접 호출 방식
 * callAndroidBridge({ bridgeName: 'MycleBridge', method: 'closeWindow' })
 * // → window.MycleBridge.closeWindow()
 *
 * callAndroidBridge({ bridgeName: 'MycleBridge', method: 'showCommercePaymentView', payload })
 * // → window.MycleBridge.showCommercePaymentView(payload)
 */
export const callAndroidBridge = ({
  bridgeName,
  method,
  payload,
}: ICallAndroidBridgeParams): void => {
  const bridge = window[bridgeName as keyof Window]

  // Mycle 방식: 메서드 직접 호출
  if (method) {
    if (!bridge || typeof bridge[method] !== 'function') {
      throw new Error(
        `Android bridge method not found: ${bridgeName}.${method}`
      )
    }

    if (payload) {
      bridge[method](payload)
      return
    }

    bridge[method]()
    return
  }

  // Partners 방식: postMessage
  if (!bridge || typeof bridge.postMessage !== 'function') {
    throw new Error(`Android bridge postMessage not found: ${bridgeName}`)
  }

  bridge.postMessage(payload)
}
